import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { Subscription } from "rxjs";
import {
  AiAgentApiService,
  AiAgentSession,
} from "services/ai-agent/ai-agent-api.service";
import {
  AiAgentChatLine,
  AiAgentChatStore,
} from "services/ai-agent/ai-agent-chat.store";
import { AiAgentCommandService } from "services/ai-agent/ai-agent-command.service";
import {
  AiAgentShellMode,
  AiAgentShellStore,
} from "services/ai-agent/ai-agent-shell.store";
import {
  exportHtmlTableToXlsx,
  isAiMdTableExportClick,
} from "services/ai-agent/ai-agent-table-xlsx";
import {
  AiAgentPendingDownload,
  isExportListExcelCommand,
  pendingDownloadFromUiCommand,
} from "services/ai-agent/ai-agent-download";
import { ExportListExcelHandler } from "services/ai-agent/handlers/export-list-excel.handler";
import { AiAgentUiContextService } from "services/ai-agent/ai-agent-ui-context.service";
import {
  AiAgentProduct,
  normalizeAiAgentProduct,
} from "services/ai-agent/ai-agent-product";
import { toast } from "@web/util/toast";
import { tr } from "@util/tr";

@Component({
  selector: "web-ai-agent-panel",
  templateUrl: "./ai-agent-panel.component.html",
  styleUrls: ["./ai-agent-panel.component.scss"],
})
export class AiAgentPanelComponent implements OnInit, OnDestroy {
  @Output() openFullPage = new EventEmitter<void>();
  @ViewChild("messagesEl") messagesEl?: ElementRef<HTMLElement>;
  @ViewChild("inputEl") inputEl?: ElementRef<HTMLInputElement>;

  open = false;
  mode: AiAgentShellMode = "docked";
  detachedX = 0;
  detachedY = 0;

  session: AiAgentSession | null = null;
  messages: AiAgentChatLine[] = [];
  input = "";
  streaming = false;
  /** True when responding GIF is missing / failed to load — show dots fallback */
  respondingGifFailed = false;
  loadingSession = false;
  error: string | null = null;
  quotaWarning: string | null = null;
  /** Session product from which FAB was opened. */
  product: AiAgentProduct = "waine";
  /** Header branding: always giada for product=giada; for waine only when ticket tools run. */
  turnGiadaBrand = false;
  /** Derived brand flag for the template (kept as a field for strictTemplates). */
  showGiadaBrand = false;
  /** Which FAB is mid expand-animation (`giada` | `waine` | null). */
  fabExpanded: AiAgentProduct | null = null;
  private fabOpenTimer: number | null = null;
  private assistantBuffer = "";
  private sessionSub?: Subscription;
  private scrollQueued = false;

  private dragging = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;

  constructor(
    private api: AiAgentApiService,
    private commands: AiAgentCommandService,
    private exportListExcel: ExportListExcelHandler,
    private chatStore: AiAgentChatStore,
    private shellStore: AiAgentShellStore,
    private uiContext: AiAgentUiContextService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const shell = this.shellStore.load();
    this.open = shell.open;
    this.mode = shell.mode;
    this.detachedX = shell.x;
    this.detachedY = shell.y;
    if (this.open) {
      this.resumeActiveOrEmpty();
    }
  }

  ngOnDestroy(): void {
    this.clearFabTimer();
    this.sessionSub?.unsubscribe();
    this.persistShell();
  }

  get isDetached(): boolean {
    return this.mode === "detached";
  }

  get isDocked(): boolean {
    return this.mode === "docked";
  }

  get canSend(): boolean {
    return !this.streaming && !this.loadingSession && !!(this.input || "").trim();
  }

  onRespondingGifError(_ev?: Event): void {
    this.respondingGifFailed = true;
  }

  /** Expand FAB logo (icon → full wordmark), then open the shell. */
  onFabClick(product: AiAgentProduct | string, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const p = normalizeAiAgentProduct(product);
    if (this.fabExpanded === p) {
      this.openProduct(p);
      return;
    }
    this.clearFabTimer();
    this.fabExpanded = p;
    this.cdr.detectChanges();
    this.fabOpenTimer = window.setTimeout(() => {
      this.fabOpenTimer = null;
      this.openProduct(p);
      this.fabExpanded = null;
    }, 420);
  }

  openProduct(product: AiAgentProduct | string): void {
    this.clearFabTimer();
    this.fabExpanded = null;
    this.product = normalizeAiAgentProduct(product);
    this.turnGiadaBrand = this.product === "giada";
    this.syncGiadaBrand();
    this.open = true;
    this.resumeActiveOrEmpty();
    setTimeout(() => {
      this.scrollToBottom(true);
      this.focusInput();
    }, 50);
    this.persistShell();
  }

  private syncGiadaBrand(): void {
    this.showGiadaBrand = this.product === "giada" || this.turnGiadaBrand;
  }

  private clearFabTimer(): void {
    if (this.fabOpenTimer != null) {
      window.clearTimeout(this.fabOpenTimer);
      this.fabOpenTimer = null;
    }
  }

  toggle(): void {
    this.onFabClick(this.product || "waine");
  }

  close(): void {
    this.clearFabTimer();
    this.fabExpanded = null;
    this.open = false;
    this.persistShell();
  }

  detach(): void {
    if (this.mode === "detached") {
      return;
    }
    const defaults = this.shellStore.defaults();
    this.mode = "detached";
    this.detachedX = defaults.x;
    this.detachedY = defaults.y;
    this.open = true;
    this.persistShell();
  }

  dock(): void {
    this.mode = "docked";
    this.open = true;
    this.persistShell();
  }

  expandFullPage(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.open = false;
    this.persistShell();
    this.openFullPage.emit();
  }

  newChat(): void {
    if (this.streaming) {
      return;
    }
    this.sessionSub?.unsubscribe();
    this.error = null;
    this.quotaWarning = null;
    this.messages = [];
    this.session = null;
    this.input = "";
    this.loadingSession = false;
    this.turnGiadaBrand = this.product === "giada";
    this.syncGiadaBrand();
    this.persistActive(null);
    this.cdr.detectChanges();
    this.focusInput();
  }

  async send(): Promise<void> {
    const text = (this.input || "").trim();
    if (!text || this.streaming || this.loadingSession) {
      return;
    }
    this.input = "";
    this.error = null;

    if (!this.session?.sessionId) {
      this.loadingSession = true;
      this.cdr.detectChanges();
      try {
        const s = await this.api.createSession(this.product).toPromise();
        if (!s?.sessionId) {
          throw new Error("Failed to create session");
        }
        this.session = s;
        this.messages = this.api.toChatLines(s);
        this.persistActive(s.sessionId);
        this.loadingSession = false;
        this.cdr.detectChanges();
      } catch (e) {
        this.loadingSession = false;
        this.input = text;
        this.error =
          (e && ((e as any).error?.message || (e as any).message)) ||
          "Failed to create session";
        this.cdr.detectChanges();
        return;
      }
    }

    await this.sendText(text);
  }

  onDragStart(event: MouseEvent): void {
    if (this.mode !== "detached") {
      return;
    }
    if ((event.target as HTMLElement)?.closest?.("button, a, ws-button")) {
      return;
    }
    this.dragging = true;
    this.dragOffsetX = event.clientX - this.detachedX;
    this.dragOffsetY = event.clientY - this.detachedY;
    event.preventDefault();
  }

  @HostListener("document:mousemove", ["$event"])
  onDragMove(event: MouseEvent): void {
    if (!this.dragging) {
      return;
    }
    const w = 400;
    const h = 560;
    const maxX = Math.max(0, window.innerWidth - w);
    const maxY = Math.max(0, window.innerHeight - 80);
    this.detachedX = Math.min(maxX, Math.max(0, event.clientX - this.dragOffsetX));
    this.detachedY = Math.min(maxY, Math.max(0, event.clientY - this.dragOffsetY));
  }

  @HostListener("document:mouseup")
  onDragEnd(): void {
    if (!this.dragging) {
      return;
    }
    this.dragging = false;
    this.persistShell();
  }

  @HostListener("click", ["$event"])
  onTableExportClick(event: MouseEvent): void {
    const table = isAiMdTableExportClick(event);
    if (table) {
      exportHtmlTableToXlsx(table);
    }
  }

  private persistShell(): void {
    this.shellStore.save({
      open: this.open,
      mode: this.mode,
      x: this.detachedX,
      y: this.detachedY,
    });
  }

  private persistActive(sessionId: string | null | undefined): void {
    try {
      this.chatStore.setActiveSessionId(
        sessionId == null ? null : String(sessionId),
        this.product
      );
    } catch {
      /* ignore */
    }
  }

  private resumeActiveOrEmpty(): void {
    this.sessionSub?.unsubscribe();
    this.turnGiadaBrand = this.product === "giada";
    this.syncGiadaBrand();
    const activeId = this.chatStore.getActiveSessionId(this.product);
    if (!activeId) {
      this.session = null;
      this.messages = [];
      this.loadingSession = false;
      this.cdr.detectChanges();
      return;
    }
    this.loadingSession = true;
    this.sessionSub = this.api.getSession(activeId, this.product).subscribe({
      next: (s) => {
        this.session = s;
        this.messages = this.api.toChatLines(s);
        this.persistActive(s.sessionId);
        this.loadingSession = false;
        this.cdr.detectChanges();
        this.scrollToBottom(true);
      },
      error: () => {
        this.persistActive(null);
        this.session = null;
        this.messages = [];
        this.loadingSession = false;
        this.cdr.detectChanges();
      },
    });
  }

  private async sendText(text: string): Promise<void> {
    if (!text || !this.session?.sessionId || this.streaming) {
      return;
    }
    this.error = null;
    if (this.product === "waine") {
      this.turnGiadaBrand = false;
      this.syncGiadaBrand();
    }
    this.messages.push({ role: "user", text });
    this.streaming = true;
    this.assistantBuffer = "";
    this.messages.push({ role: "assistant", text: "" });
    const assistantIndex = this.messages.length - 1;
    this.cdr.detectChanges();
    this.scrollToBottom(true);

    try {
      await this.api.streamMessage(
        String(this.session.sessionId),
        text,
        (event, data) => {
          if (event === "delta" && data?.text) {
            this.assistantBuffer += data.text;
            const prev = this.messages[assistantIndex];
            this.messages[assistantIndex] = {
              role: "assistant",
              text: this.assistantBuffer,
              downloads: prev?.downloads,
            };
            this.cdr.detectChanges();
            this.scrollToBottom();
          } else if (event === "branding") {
            if (data?.persona === "giada" && this.product === "waine") {
              this.turnGiadaBrand = true;
              this.syncGiadaBrand();
              this.cdr.detectChanges();
            }
          } else if (event === "ui_command") {
            if (isExportListExcelCommand(data)) {
              this.attachDownload(assistantIndex, pendingDownloadFromUiCommand(data));
            } else {
              this.commands.handle(data, {
                resendMessage: (msg) => this.sendText(msg),
              });
            }
          } else if (event === "quota_warning") {
            this.quotaWarning =
              data?.message || "Approaching AI agent daily quota";
            this.cdr.detectChanges();
          } else if (event === "error") {
            this.error = data?.message || "Agent error";
            this.cdr.detectChanges();
          } else if (event === "done") {
            if (this.product === "waine") {
              this.turnGiadaBrand = false;
              this.syncGiadaBrand();
              this.cdr.detectChanges();
            }
          }
        },
        this.uiContext.snapshot(),
        this.product
      );
    } catch (e) {
      this.error = (e && (e as any).message) || "Stream failed";
      if (!this.assistantBuffer) {
        this.messages.pop();
      }
    } finally {
      this.streaming = false;
      if (this.product === "waine") {
        this.turnGiadaBrand = false;
        this.syncGiadaBrand();
      }
      this.cdr.detectChanges();
      this.scrollToBottom(true);
      this.focusInput();
    }
  }

  onDownloadClick(dl: AiAgentPendingDownload, event?: Event): void {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const cmd = dl?.command;
    if (!cmd?.type) {
      void toast(tr("Error"));
      return;
    }
    // Direct call (same path as CRM list Excel) — avoid silent no-op if dispatch misses.
    if (cmd.type === "exportListExcel") {
      void Promise.resolve(
        this.exportListExcel.handle({
          type: "exportListExcel",
          list: cmd.list != null ? String(cmd.list) : "",
          filters:
            cmd.filters && typeof cmd.filters === "object"
              ? { ...(cmd.filters as Record<string, unknown>) }
              : {},
        })
      ).catch((err) => {
        console.error("[ai-agent] exportListExcel failed", err);
        void toast(tr("Error"));
      });
      return;
    }
    this.commands.handle(cmd);
  }

  private attachDownload(
    assistantIndex: number,
    dl: AiAgentPendingDownload | null
  ): void {
    if (!dl || assistantIndex < 0 || assistantIndex >= this.messages.length) {
      return;
    }
    const msg = this.messages[assistantIndex];
    const downloads = [...(msg.downloads || []), dl];
    this.messages[assistantIndex] = { ...msg, downloads };
    this.cdr.detectChanges();
    this.scrollToBottom();
  }

  private focusInput(): void {
    requestAnimationFrame(() => {
      try {
        this.inputEl?.nativeElement?.focus?.();
      } catch {
        /* ignore */
      }
    });
  }

  private scrollToBottom(force = false): void {
    if (this.scrollQueued && !force) {
      return;
    }
    this.scrollQueued = true;
    requestAnimationFrame(() => {
      this.scrollQueued = false;
      const el = this.messagesEl?.nativeElement;
      if (!el) {
        return;
      }
      try {
        el.scrollTop = el.scrollHeight;
        if (force) {
          requestAnimationFrame(() => {
            el.scrollTop = el.scrollHeight;
          });
        }
      } catch {
        /* ignore */
      }
    });
  }
}
